import * as Api from '@ViewModels';
import { Button } from '@WebComponents/Button';
import { Checkbox } from '@WebComponents/Checkbox';
import { css } from 'aphrodite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
	invalidateIntegrationQuery,
	useGetHousecallProLocationsQuery,
	usePostExternalIntegrationConfigurationMutation,
} from '../../../../../../../../../queries';
import { LoadingSpinner } from '../../../../../../../../../web/components/LoadingSpinner';
import { SettingGroup } from '../SettingGroup';
import { styleSheet } from './styles';

function formatAddress(address: Api.IHousecallProLocationAddress): string {
	const parts = [address.street, address.street_line_2, address.city, address.state, address.zip].filter(Boolean);
	return parts.join(', ');
}

interface IHousecallProLocationSetupProps {
	impersonationContext: Api.IImpersonationContext;
	configuration: Api.IIntegrationConfiguration;
	onConfigurationSaved: () => void;
}

export const HousecallProLocationSetup: React.FC<IHousecallProLocationSetupProps> = ({
	impersonationContext,
	configuration,
	onConfigurationSaved,
}) => {
	const hcpConfig = configuration as Api.IHousecallProIntegrationConfiguration;
	const isConnected = !!hcpConfig?.accountConnection?.isConnected;
	const isMultiLocation = hcpConfig?.isMultiLocation === true;
	const needsConfiguration = hcpConfig?.needsConfiguration !== false;
	const currentTenantIds = useMemo(() => hcpConfig?.tenantIds || [], [hcpConfig?.tenantIds]);
	const currentSyncAll = hcpConfig?.syncAllLocations === true;
	const isConfigured = !needsConfiguration;

	const [isEditing, setIsEditing] = useState(false);
	const [syncAllLocations, setSyncAllLocations] = useState(true);
	const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(new Set());

	const locationsQuery = useGetHousecallProLocationsQuery({
		impersonationContext,
		enabled: isMultiLocation,
	});

	const configMutation = usePostExternalIntegrationConfigurationMutation({
		impersonationContext,
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to save location configuration');
		},
		onSuccess: () => {
			toast.success('Location configuration saved successfully');
		},
	});

	// Initialize form state from current configuration
	useEffect(() => {
		if (currentSyncAll) {
			setSyncAllLocations(true);
			setSelectedLocationIds(new Set());
		} else if (currentTenantIds.length > 0) {
			setSyncAllLocations(false);
			setSelectedLocationIds(new Set(currentTenantIds));
		}
	}, [currentSyncAll, currentTenantIds]);

	const toggleLocation = (locationId: string) => {
		setSelectedLocationIds(prev => {
			const next = new Set(prev);
			if (next.has(locationId)) {
				next.delete(locationId);
			} else {
				next.add(locationId);
			}
			return next;
		});
	};

	const onSave = useCallback(async () => {
		const body: Partial<Api.IHousecallProIntegrationConfiguration> = syncAllLocations
			? { syncAllLocations: true }
			: { tenantIds: Array.from(selectedLocationIds) };

		if (!syncAllLocations && selectedLocationIds.size === 0) {
			toast.error('Please select at least one location or choose to sync all.');
			return;
		}

		try {
			await configMutation.mutateAsync({
				integrationProvider: Api.IntegrationProvider.HousecallPro,
				configuration: body,
			});
			invalidateIntegrationQuery({
				impersonationContext,
				provider: Api.IntegrationProvider.HousecallPro,
			});
			onConfigurationSaved();
			setIsEditing(false);
		} catch {
			// Error handled in mutation onError
		}
	}, [syncAllLocations, selectedLocationIds, configMutation, impersonationContext, onConfigurationSaved]);

	const onCancel = useCallback(() => {
		setIsEditing(false);
		if (currentSyncAll) {
			setSyncAllLocations(true);
			setSelectedLocationIds(new Set());
		} else {
			setSyncAllLocations(false);
			setSelectedLocationIds(new Set(currentTenantIds));
		}
	}, [currentSyncAll, currentTenantIds]);

	const onEdit = useCallback(() => {
		setIsEditing(true);
	}, []);

	if (!isConnected) {
		return (
			<SettingGroup title='Location Configuration'>
				<div className={css(styleSheet.section)}>
					<p className={css(styleSheet.description)}>
						The customer must enter their Housecall Pro API key before location setup can be completed.
					</p>
				</div>
			</SettingGroup>
		);
	}

	if (!isMultiLocation) {
		const singleLocation = locationsQuery.data?.locations?.[0];
		return (
			<SettingGroup title='Location Configuration'>
				<div className={css(styleSheet.section)}>
					<p className={css(styleSheet.description)}>
						This is a single-location account. No additional location configuration is needed.
					</p>
					{singleLocation ? (
						<div className={css(styleSheet.locationCard)}>
							<div className={css(styleSheet.locationCardInfo)}>
								<span className={css(styleSheet.locationCardName)}>{singleLocation.name}</span>
								{singleLocation.address ? (
									<span className={css(styleSheet.locationCardDetail)}>{formatAddress(singleLocation.address)}</span>
								) : null}
								{singleLocation.phoneNumber ? (
									<span className={css(styleSheet.locationCardDetail)}>{singleLocation.phoneNumber}</span>
								) : null}
							</div>
						</div>
					) : null}
				</div>
			</SettingGroup>
		);
	}

	if (locationsQuery.isPending) {
		return (
			<SettingGroup title='Location Configuration'>
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='small' />
				</div>
			</SettingGroup>
		);
	}

	const locations = locationsQuery.data?.locations || [];

	const renderConfigured = () => (
		<div className={css(styleSheet.section)}>
			<p className={css(styleSheet.description)}>
				This account has multiple locations. The following locations are configured to sync.
			</p>
			<div className={css(styleSheet.configuredSection)}>
				<div className={css(styleSheet.configuredLabel)}>Syncing:</div>
				<div className={css(styleSheet.configuredValue)}>
					{currentSyncAll ? 'All locations' : `${currentTenantIds.length} of ${locations.length} locations`}
				</div>
			</div>
			{!currentSyncAll && currentTenantIds.length > 0 && (
				<div className={css(styleSheet.locationList)}>
					{locations
						.filter(loc => currentTenantIds.includes(loc.id))
						.map(location => (
							<div key={location.id} className={css(styleSheet.locationCard)}>
								<div className={css(styleSheet.locationCardInfo)}>
									<span className={css(styleSheet.locationCardName)}>
										{location.name}
										{location.isPrimary ? ' (Primary)' : ''}
									</span>
									{location.address ? (
										<span className={css(styleSheet.locationCardDetail)}>{formatAddress(location.address)}</span>
									) : null}
								</div>
							</div>
						))}
				</div>
			)}
			<div className={css(styleSheet.buttonGroup)}>
				<Button kind='reverse' onClick={onEdit}>
					Edit
				</Button>
			</div>
		</div>
	);

	const renderEditing = () => (
		<div id='requirement-target-HousecallPro-locations' className={css(styleSheet.section)}>
			<p className={css(styleSheet.description)}>
				Select which locations to sync with Levitate, or choose to sync all locations.
			</p>

			<div className={css(styleSheet.checkboxRow)}>
				<Checkbox
					id='hcp-sync-all-locations'
					checked={syncAllLocations}
					onChange={e => setSyncAllLocations((e.target as HTMLInputElement).checked)}
					disabled={configMutation.isPending}
				/>
				<label htmlFor='hcp-sync-all-locations' className={css(styleSheet.checkboxLabel)}>
					Sync all locations
				</label>
			</div>

			{!syncAllLocations && (
				<div className={css(styleSheet.locationList)}>
					{locations.map(location => {
						const checkboxId = `hcp-location-${location.id}`;
						return (
							<div key={location.id} className={css(styleSheet.locationCard)}>
								<div className={css(styleSheet.locationCardHeader)}>
									<Checkbox
										id={checkboxId}
										checked={selectedLocationIds.has(location.id)}
										onChange={() => toggleLocation(location.id)}
										disabled={configMutation.isPending}
									/>
									<div className={css(styleSheet.locationCardInfo)}>
										<label htmlFor={checkboxId} className={css(styleSheet.locationCardName)}>
											{location.name}
											{location.isPrimary ? ' (Primary)' : ''}
										</label>
										{location.address ? (
											<span className={css(styleSheet.locationCardDetail)}>{formatAddress(location.address)}</span>
										) : null}
										{location.phoneNumber ? (
											<span className={css(styleSheet.locationCardDetail)}>{location.phoneNumber}</span>
										) : null}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			<div className={css(styleSheet.buttonGroup)} aria-busy={configMutation.isPending}>
				<Button
					kind='primary'
					onClick={onSave}
					disabled={configMutation.isPending || (!syncAllLocations && selectedLocationIds.size === 0)}
				>
					{configMutation.isPending ? 'Saving...' : 'Save'}
				</Button>
				{isConfigured && (
					<Button kind='reverse' onClick={onCancel} disabled={configMutation.isPending}>
						Cancel
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<SettingGroup title='Location Configuration'>
			{isEditing || !isConfigured ? renderEditing() : renderConfigured()}
		</SettingGroup>
	);
};
